import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import api from '../interceptors/api';

const centeredFullscreen = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: 24,
  textAlign: 'center',
};

const fullViewportIframe = {
  position: 'fixed',
  inset: 0,
  width: '100vw',
  height: '100vh',
  border: 0,
};

export default function HtmlPlaygroundPreview() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { t } = useTranslation();
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError('missing_id');
      return undefined;
    }
    let cancelled = false;
    api
      .get(`/html-playground/share/${id}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) setHtml(res.data.html_code || '');
        else setError(res.data?.error || 'not_found');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || 'load_failed');
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    document.title = t('html_playground_preview_title');
  }, [t]);

  // Each ?id is a unique, user-generated full-viewport render — not a
  // canonical landing page. Keep search engines off of these URLs so they
  // don't pollute SERP with low-quality duplicates of /tools/html-playground.
  // robots.txt also disallows this path; the meta is a belt-and-suspenders
  // for any crawler that reaches the route via an external link.
  const noindex = (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );

  if (error) {
    const message =
      error === 'missing_id' || error === 'not_found'
        ? t('html_playground_preview_not_found')
        : error === 'load_failed'
        ? t('html_playground_preview_load_failed')
        : error;
    return (
      <>
        {noindex}
        <div style={centeredFullscreen}>
          <div>
            <h2>{t('html_playground_preview_unavailable')}</h2>
            <p style={{ color: '#666' }}>{message}</p>
          </div>
        </div>
      </>
    );
  }

  // No loader: stay blank until the stored HTML arrives, then render it directly.
  if (html === null) return noindex;

  return (
    <>
      {noindex}
      <iframe
        title={t('html_playground_preview_title')}
        srcDoc={html}
        sandbox="allow-scripts allow-forms allow-popups allow-modals"
        style={fullViewportIframe}
      />
    </>
  );
}
